import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { isProjectRoom } from "shared/types/Project"
import { Room } from "shared/types/Room"
import { SelectOption } from "shared/types/Select"
import { getFlattenedTree } from "shared/ui/table/utils/getFlattenedTree"

import { useProjectData } from "entities/projects/api/useProjectData"

export function useRoomOptions(projectID: string | null): {
  options: SelectOption<string, Room>[]
  isLoading: boolean
} {
  const { t } = useTranslation()
  const { project, isLoading } = useProjectData(projectID, true)

  const flattenedChildren = useMemo(
    () =>
      project?.id === projectID ? getFlattenedTree(project?.children) : [],
    [projectID, project?.id, project?.children]
  )

  const options = useMemo(() => {
    if (!projectID) return []

    const result =
      flattenedChildren?.reduce((acc: SelectOption<string, Room>[], child) => {
        if (isProjectRoom(child)) {
          return [
            ...acc,
            {
              key: child.id,
              label: child.label,
              value: child.id,
              data: child
            }
          ] as SelectOption<string, Room>[]
        }
        return acc
      }, []) || []

    result.unshift({ value: projectID, label: t("Room.NoRoom") })
    return result
  }, [flattenedChildren, t])

  return { options, isLoading }
}
